/**
 * Nút "Chạy luồng" trên hồ sơ khách: liệt kê các luồng kích hoạt "Nhân viên
 * bấm nút" đang bật; chọn một luồng → xác nhận → chạy THẬT với khách này.
 * Ẩn hẳn khi người dùng không có quyền `automation.run` hoặc chưa có luồng nào.
 */
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronDown, Loader2, MousePointerClick } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useManualRules, useRunManualRule, type ManualRule } from '@/hooks/use-automation'

/** Hộp xác nhận + chạy luồng thủ công với một khách (dùng chung nút "Chạy luồng" và mục trong menu Hành động). */
export function ChayLuongHopXacNhan({ rule, contactId, tenKhach, onClose }: { rule: ManualRule | null; contactId: string; tenKhach: string; onClose: () => void }) {
  const { t } = useTranslation('autoTemplates')
  const run = useRunManualRule()
  const chon = rule
  const chay = () => {
    if (!chon) return
    run.mutate({ ruleId: chon.id, contactId }, {
      onSuccess: (kq) => {
        onClose()
        if (kq.filtered) { toast.warning(t('chayTay.toast.filtered', { name: chon.name, reason: kq.filterReason ?? t('chayTay.toast.defaultReason') })); return }
        const boQua = kq.nodeResults.filter((r) => r.status === 'skipped')
        if (kq.errorNodes > 0) toast.error(t('chayTay.toast.errorSteps', { name: chon.name, count: kq.errorNodes }))
        else toast.success(t('chayTay.toast.done', { name: chon.name, steps: kq.executedNodes, skipped: boQua.length ? t('chayTay.toast.skipped', { count: boQua.length }) : '', waiting: kq.waiting ? t('chayTay.toast.waiting') : '' }))
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }
  return (
    <Dialog open={!!chon} onOpenChange={(v) => { if (!v && !run.isPending) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{chon?.buttonLabel}</DialogTitle>
          <DialogDescription>
            <Trans t={t} i18nKey="chayTay.confirm" values={{ name: chon?.name, customer: tenKhach }} components={{ b: <b /> }} />
            {chon?.description ? ` ${chon.description}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={run.isPending} onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button disabled={run.isPending} onClick={chay}>{run.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('chayTay.run')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ChayLuongThuCong({ contactId, tenKhach }: { contactId: string; tenKhach: string }) {
  const { t } = useTranslation('autoTemplates')
  const { data: rules } = useManualRules()
  const run = useRunManualRule()
  const [chon, setChon] = useState<ManualRule | null>(null)
  if (!rules?.length) return null

  const chay = () => {
    if (!chon) return
    run.mutate({ ruleId: chon.id, contactId }, {
      onSuccess: (kq) => {
        setChon(null)
        if (kq.filtered) { toast.warning(t('chayTay.toast.filtered', { name: chon.name, reason: kq.filterReason ?? t('chayTay.toast.defaultReason') })); return }
        const boQua = kq.nodeResults.filter((r) => r.status === 'skipped')
        if (kq.errorNodes > 0) toast.error(t('chayTay.toast.errorSteps', { name: chon.name, count: kq.errorNodes }))
        else toast.success(t('chayTay.toast.done', { name: chon.name, steps: kq.executedNodes, skipped: boQua.length ? t('chayTay.toast.skipped', { count: boQua.length }) : '', waiting: kq.waiting ? t('chayTay.toast.waiting') : '' }))
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline"><MousePointerClick className="h-4 w-4" /> {t('chayTay.button')} <ChevronDown className="h-3.5 w-3.5 opacity-60" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {rules.map((r) => (
            <DropdownMenuItem key={r.id} onSelect={() => setChon(r)} className="flex-col items-start gap-0">
              <span className="font-medium">{r.buttonLabel}</span>
              {r.buttonLabel !== r.name && <span className="text-[11px] text-muted-foreground">{r.name}</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!chon} onOpenChange={(v) => { if (!v && !run.isPending) setChon(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{chon?.buttonLabel}</DialogTitle>
            <DialogDescription>
              <Trans t={t} i18nKey="chayTay.confirm" values={{ name: chon?.name, customer: tenKhach }} components={{ b: <b /> }} />
              {chon?.description ? ` ${chon.description}` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={run.isPending} onClick={() => setChon(null)}>{t('common:actions.cancel')}</Button>
            <Button disabled={run.isPending} onClick={chay}>{run.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('chayTay.run')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
